import { Link, Navigate, useParams } from 'react-router-dom';
import {
  AccessTime,
  CalendarToday,
  Event,
  Input as InputIcon,
  Output as OutputIcon,
  Psychology,
  Videocam,
} from '@mui/icons-material';
import { Badge, Button, Card, CardContent, CardHeader, Chip } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { CardTitle } from '../components/card-title';
import { DebugCard } from '../components/debug-card';
import { DetailRow, DateValue } from '../components/detail-row';
import { EncounterAnalysisCard } from '../components/encounter-analysis-card';
import { EncounterTelemetryCard } from '../components/encounter-telemetry-card';
import { LoadingState } from '../components/loading-state';
import { ExplorableId } from '../components/explorable-id';
import { RecentActivityCard } from '../components/recent-activity-card';
import { IdChip } from '../components/org-page-redesign/primitives';
import {
  useGetEncounterDetailsQuery,
  useGetProviderNoteProcessingSessionsQuery,
} from '../store/api';
import { TextInputReveal } from '../components/text-input-reveal';
import { formatDateTime } from '../utils/formatting';

// Badge color helper function
const getStatusBadgeColor = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'success':
    case 'finished':
      return 'bg-green-100 text-green-800';
    case 'pending':
    case 'in_progress':
    case 'processing':
      return 'bg-yellow-100 text-yellow-800';
    case 'failed':
    case 'error':
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const MODALITY_LABELS: Record<string, string> = {
  in_person: 'In person',
  video: 'Video',
  audio: 'Audio',
};

function resourceExplorerTarget(
  doc: any,
  context: 'inputs' | 'outputs',
): { type: string; id: string } | null {
  // note outputs carry the note_id itself; migrated "document" outputs carry
  // a document_reference_id. Recording inputs only become linkable once the
  // backend resolves their transcription_session_id from encounter_documents.
  if (context === 'outputs' && doc.encounter_document_id) {
    if (doc.type === 'note') return { type: 'note', id: doc.encounter_document_id };
    if (doc.type === 'document')
      return { type: 'document_reference', id: doc.encounter_document_id };
  }
  if (context === 'inputs' && doc.type === 'recording' && doc.transcription_session_id) {
    return { type: 'transcription_session', id: doc.transcription_session_id };
  }
  return null;
}

function DocumentList({
  documents,
  emptyMessage,
  context,
  organizationId,
  encounterId,
}: {
  documents: any[] | undefined;
  emptyMessage: string;
  context: 'inputs' | 'outputs';
  organizationId?: string | null;
  encounterId?: string;
}) {
  if (!documents || documents.length === 0) {
    return <p className="text-sm text-gray-500">{emptyMessage}</p>;
  }
  return (
    <div className="space-y-3">
      {documents.map((doc, index) => {
        const docId = doc.encounter_document_id || doc.jotdoc_id || `doc-${index}`;
        const target = resourceExplorerTarget(doc, context);
        const explorerUrl = target
          ? `/admin-v2/resources/${target.type}/${target.id}?organization_id=${encodeURIComponent(organizationId || '')}`
          : null;
        return (
          <div key={docId} className="border rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Chip size="small" label={doc.type || 'unknown'} />
                <span className="text-sm font-medium text-gray-900">{doc.title || 'Untitled'}</span>
              </div>
              <Badge className={getStatusBadgeColor(doc.status)}>{doc.status || 'unknown'}</Badge>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              {explorerUrl && target?.id === docId ? (
                <Link
                  to={explorerUrl}
                  className="font-mono break-all text-blue-600 hover:underline"
                >
                  {docId}
                </Link>
              ) : (
                <span className="font-mono break-all">{docId}</span>
              )}
              {doc.created_at && <span>{formatDateTime(doc.created_at)}</span>}
            </div>
            {explorerUrl && target?.id !== docId && (
              <div className="mt-1 text-xs">
                <Link
                  to={explorerUrl}
                  className="font-mono break-all text-blue-600 hover:underline"
                >
                  session {target?.id}
                </Link>
              </div>
            )}
            {context === 'inputs' &&
              doc.type === 'text' &&
              doc.encounter_document_id &&
              encounterId && (
                <TextInputReveal
                  encounterId={encounterId}
                  encounterDocumentId={doc.encounter_document_id}
                />
              )}
          </div>
        );
      })}
    </div>
  );
}

export default function EncounterDetailPage() {
  const {
    providerId: urlProviderId,
    encounterId: encounterIdParam,
    id: legacyId,
  } = useParams<{ providerId?: string; encounterId?: string; id?: string }>();
  const encounterId = encounterIdParam || legacyId || '';

  // Always call the encounter details query
  const { data, isLoading } = useGetEncounterDetailsQuery({ encounterId }, { skip: !encounterId });

  // Prefer the provider from the URL; fall back to the encounter response
  const providerId = urlProviderId
    ? decodeURIComponent(urlProviderId)
    : data?.provider?.provider_id || data?.encounter?.provider_id;

  // Get related note processing sessions for this encounter
  const { data: npsData } = useGetProviderNoteProcessingSessionsQuery(
    {
      providerId: providerId || '',
      encounterId: encounterId,
      perPage: 10,
    },
    { skip: !providerId || !encounterId },
  );

  // If we don't have the required encounterId, show error
  if (!encounterId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Encounter not found</h2>
            <p className="text-gray-600 mt-2">Invalid encounter ID.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading encounter details..." />;
  }

  if (!data?.encounter) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Encounter not found</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Legacy /admin-v2/encounter/:id URL — redirect to the provider-scoped URL
  if (!urlProviderId && providerId) {
    return (
      <Navigate
        to={`/admin-v2/provider/${encodeURIComponent(providerId)}/encounters/${encounterId}`}
        replace
      />
    );
  }

  const { encounter, appointment, transcriptions, notes } = data;
  const telehealth = encounter.telehealth;

  const telehealthChip =
    telehealth?.telehealth_kind === 'jotmeet'
      ? { label: 'Telehealth · JotMeet', color: 'primary' as const }
      : telehealth?.telehealth_kind === 'external_telehealth'
        ? { label: 'Telehealth · External', color: 'info' as const }
        : { label: 'In-person', color: 'default' as const };

  return (
    <>
      {/* Encounter header strip; provider identity lives in the passport above */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Event className="mr-2 h-6 w-6" />
              Encounter
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <ExplorableId
                label="encounter"
                value={encounter.encounter_id}
                resourceType="encounter"
                organizationId={encounter.organization_id}
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Chip
              icon={
                telehealth?.telehealth_kind && telehealth.telehealth_kind !== 'in_person' ? (
                  <Videocam />
                ) : undefined
              }
              label={telehealthChip.label}
              color={telehealthChip.color}
              size="small"
            />
            <Badge className={getStatusBadgeColor(encounter.status)}>
              {encounter.status || 'unknown'}
            </Badge>
            {providerId && (
              <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}/encounters`}>
                <Button variant="outlined" size="small">
                  Back to Encounters
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Telemetry Summary */}
      <div className="mt-6 mb-6">
        <EncounterTelemetryCard encounterId={encounterId} />
      </div>

      {/* Encounter Analysis */}
      {encounter.encounter_analysis && (
        <div className="mt-6 mb-6">
          <EncounterAnalysisCard analysis={encounter.encounter_analysis} />
        </div>
      )}

      {/* Debug Card */}
      <DebugCard
        transcriptionId={transcriptions?.[0]?.transcription_session_id}
        noteProcessingId={npsData?.items?.[0]?.note_processing_session_id}
        noteId={notes?.[0]?.note_id}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Times & Dates */}
        <Card>
          <CardHeader
            title={
              <CardTitle className="flex items-center">
                <AccessTime className="mr-2 h-5 w-5" />
                Times &amp; Dates
              </CardTitle>
            }
          />
          <CardContent>
            <div className="space-y-2">
              <DetailRow label="Created" value={<DateValue value={encounter.created_at} />} />
              <DetailRow
                label="Scheduled for"
                value={
                  encounter.scheduled_for ? (
                    <>
                      <DateValue value={encounter.scheduled_for} />
                      {encounter.scheduled_duration ? ` (${encounter.scheduled_duration} min)` : ''}
                    </>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )
                }
              />
              <DetailRow label="Start time" value={<DateValue value={encounter.start_time} />} />
              <DetailRow label="End time" value={<DateValue value={encounter.end_time} />} />
              <DetailRow
                label="Time source"
                value={encounter.time_source || <span className="text-gray-400">—</span>}
              />
              <DetailRow
                label="Date of service"
                value={encounter.date_of_service || <span className="text-gray-400">—</span>}
              />
              <DetailRow
                label="Service rendered at"
                value={<DateValue value={encounter.service_rendered_at} />}
              />
              <DetailRow
                label="Signed"
                value={
                  encounter.is_signed ? (
                    <span className="text-green-700">
                      <DateValue value={encounter.signed_at} />
                    </span>
                  ) : (
                    <span className="text-gray-400">Not signed</span>
                  )
                }
              />
              <DetailRow label="Updated" value={<DateValue value={encounter.updated_at} />} />
              <DetailRow label="Modified" value={<DateValue value={encounter.modified_at} />} />
            </div>
          </CardContent>
        </Card>

        {/* Session / Telehealth */}
        <Card>
          <CardHeader
            title={
              <CardTitle className="flex items-center">
                <Videocam className="mr-2 h-5 w-5" />
                Session
              </CardTitle>
            }
          />
          <CardContent>
            <div className="space-y-2">
              <DetailRow
                label="Encounter type"
                value={encounter.encounter_type || <span className="text-gray-400">—</span>}
              />
              <DetailRow
                label="Modality"
                value={
                  encounter.modality ? (
                    MODALITY_LABELS[encounter.modality] || encounter.modality
                  ) : (
                    <span className="text-gray-400">—</span>
                  )
                }
              />
              <DetailRow
                label="Place of service"
                value={encounter.place_of_service || <span className="text-gray-400">—</span>}
              />
              <DetailRow label="Telehealth" value={telehealthChip.label} />
              {telehealth?.jotmeet_intent != null && (
                <DetailRow
                  label="JotMeet intent"
                  value={telehealth.jotmeet_intent ? 'Yes' : 'No'}
                />
              )}
              <DetailRow
                label="JotMeet room"
                value={
                  telehealth?.jotmeet_room_created ? (
                    `Created${telehealth.jotmeet_room_status ? ` (${telehealth.jotmeet_room_status})` : ''}`
                  ) : (
                    <span className="text-gray-400">None</span>
                  )
                }
              />
              <DetailRow
                label="Patient ID"
                value={
                  encounter.patient_id ? (
                    <IdChip label="patient" value={encounter.patient_id} />
                  ) : (
                    <span className="text-gray-400">—</span>
                  )
                }
              />
              <DetailRow
                label="Template ID"
                value={
                  encounter.template_id ? (
                    <IdChip label="template" value={encounter.template_id} />
                  ) : (
                    <span className="text-gray-400">—</span>
                  )
                }
              />
              <DetailRow
                label="Note"
                value={
                  encounter.note_id ? (
                    <Link
                      to={
                        providerId
                          ? `/admin-v2/provider/${encodeURIComponent(providerId)}/notes/${encounter.note_id}`
                          : `/admin-v2/note/${encounter.note_id}`
                      }
                      className="text-blue-600 font-mono"
                    >
                      {encounter.note_id.substring(0, 8)}...
                    </Link>
                  ) : (
                    <span className="text-gray-400">None</span>
                  )
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Inputs */}
        <Card>
          <CardHeader
            title={
              <CardTitle className="flex items-center">
                <InputIcon className="mr-2 h-5 w-5" />
                Inputs ({encounter.inputs?.length ?? 0})
              </CardTitle>
            }
          />
          <CardContent>
            <DocumentList
              documents={encounter.inputs}
              emptyMessage="No input documents on this encounter."
              context="inputs"
              encounterId={encounterId}
              organizationId={encounter.organization_id}
            />
          </CardContent>
        </Card>

        {/* Outputs */}
        <Card>
          <CardHeader
            title={
              <CardTitle className="flex items-center">
                <OutputIcon className="mr-2 h-5 w-5" />
                Outputs ({encounter.outputs?.length ?? 0})
              </CardTitle>
            }
          />
          <CardContent>
            <DocumentList
              documents={encounter.outputs}
              emptyMessage="No output documents on this encounter."
              context="outputs"
              organizationId={encounter.organization_id}
            />
          </CardContent>
        </Card>

        {/* Appointment Information */}
        {appointment ? (
          <Card>
            <CardHeader
              title={
                <CardTitle className="flex items-center">
                  <CalendarToday className="mr-2 h-5 w-5" />
                  Associated Appointment
                </CardTitle>
              }
            />
            <CardContent>
              <div className="space-y-2">
                <DetailRow
                  label="Appointment ID"
                  value={<IdChip label="appt" value={appointment.appointment_id} />}
                />
                <DetailRow label="Type" value={appointment.encounter_type || '—'} />
                <DetailRow label="Scheduled" value={<DateValue value={appointment.start_time} />} />
                <DetailRow
                  label="Status"
                  value={
                    <Badge className={getStatusBadgeColor(appointment.status)}>
                      {appointment.status}
                    </Badge>
                  }
                />
                <DetailRow label="Telehealth" value={appointment.telehealth ? 'Yes' : 'No'} />
                {appointment.use_jotmeet != null && (
                  <DetailRow label="Uses JotMeet" value={appointment.use_jotmeet ? 'Yes' : 'No'} />
                )}
              </div>
              <div className="mt-4">
                <Link
                  to={
                    providerId
                      ? `/admin-v2/provider/${encodeURIComponent(providerId)}/appointments/${appointment.appointment_id}`
                      : `/admin-v2/appointment/${appointment.appointment_id}`
                  }
                >
                  <Button variant="outlined" size="small" className="w-full">
                    View Appointment Details
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title={
                <CardTitle className="flex items-center">
                  <CalendarToday className="mr-2 h-5 w-5" />
                  Associated Appointment
                </CardTitle>
              }
            />
            <CardContent>
              <p className="text-sm text-gray-500">
                This encounter is not associated with an appointment.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Note Processing Sessions Information */}
      {npsData && npsData.items && npsData.items.length > 0 && (
        <div className="bg-white rounded-lg shadow mt-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Psychology className="mr-2 h-5 w-5" />
              Note Processing Sessions ({npsData.items.length})
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {npsData.items.map((nps: any) => (
                <div key={nps.note_processing_session_id} className="border rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Session ID:</span>
                      <span className="text-sm text-gray-900">
                        {nps.note_processing_session_id.substring(0, 8)}...
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Status:</span>
                      <Badge className={getStatusBadgeColor(nps.status)}>
                        {nps.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Transcripts:</span>
                      <span className="text-sm text-gray-900">
                        {nps.transcription_session_ids?.length || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Note:</span>
                      <span className="text-sm text-gray-900">
                        {nps.note_id ? `${nps.note_id.substring(0, 8)}...` : 'No note'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Created:</span>
                      <span className="text-sm text-gray-900">
                        {formatDateTime(nps.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Link
                      to={`/admin-v2/note-processing-session/${nps.note_processing_session_id}`}
                    >
                      <Button variant="outlined" size="small">
                        View Session Details
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Activity: everything the analytics store recorded about this
          encounter — including events about its notes and transcription
          sessions, stitched together server-side. */}
      {encounter.organization_id && (
        <div className="mt-6">
          <RecentActivityCard
            organizationId={encounter.organization_id}
            encounterId={encounterId}
          />
        </div>
      )}
    </>
  );
}
